import {
    observable, makeObservable, action,
} from 'mobx'
import * as R from 'ramda'
import { v4 as uuidv4 } from 'uuid'
import NewWidgetMetric from './NewWidgetMetric'

class NewWidget {
    @observable widgetBasis = 'question'

    @observable industry

    @observable respondentType

    @observable surveyType

    @observable productType

    @observable questionId

    @observable parentQuestion

    @observable kpiType // @observable displayAs, @observable nonQuestionKpiType

    @observable questionTypeCode

    @observable isNumeric

    @observable question

    @observable scope // @observable dataLevel

    @observable benchmarkType // @observable timePeriod

    @observable filter = []

    @observable chartType // @observable chart

    @observable width = 2 // @observable defaultSize = 2

    @observable multiQuestion = [{}, {}]

    @observable nonQuestionType

    @observable quadrant

    @observable title // @observable widgetTitle

    @observable widgetRenderSettings = {}

    @observable qlistParentId

    @observable customTag

    @observable tagOptions = []

    @observable optionFilter = []

    @observable includeRegistrationData = false

    @observable isMatrixKpi = false

    @observable metrics = []

    @observable data

    @observable refreshKey = ''

    @observable editing = false

    @observable branchFilter = []

    previousKey = undefined

    constructor(
        widget = {},
        store,
        appStore,
    ) {
        const {
            widgetBasis,
            industry,
            respondentType,
            surveyType,
            productType,
            questionId,
            parentQuestion,
            kpiType,
            questionTypeCode,
            isNumeric,
            question,
            scope,
            benchmarkType,
            filter,
            chartType,
            width,
            multiQuestion,
            nonQuestionType,
            quadrant,
            title,
            widgetRenderSettings,
            qlistParentId,
            customTag,
            tagOptions,
            optionFilter,
            includeRegistrationData,
            isMatrixKpi,
            editing,
            previousKey,
            branchFilter,
        } = widget
        makeObservable(this)
        this.widgetBasis = widgetBasis || 'question'
        this.industry = industry
        this.respondentType = respondentType
        this.surveyType = surveyType
        this.productType = productType
        this.questionId = questionId
        this.parentQuestion = parentQuestion
        this.kpiType = kpiType
        this.questionTypeCode = questionTypeCode
        this.isNumeric = isNumeric
        this.question = question
        this.scope = scope
        this.benchmarkType = benchmarkType
        this.filter = filter || []
        this.chartType = chartType
        this.width = width || 2
        this.multiQuestion = multiQuestion || [{}, {}]
        this.nonQuestionType = nonQuestionType
        this.quadrant = quadrant
        this.title = title
        this.widgetRenderSettings = widgetRenderSettings || {}
        this.qlistParentId = qlistParentId
        this.customTag = customTag
        this.tagOptions = tagOptions || []
        this.optionFilter = optionFilter || []
        this.branchFilter = branchFilter || []
        this.includeRegistrationData = includeRegistrationData || false
        this.isMatrixKpi = isMatrixKpi || false
        this.editing = editing
        this.previousKey = previousKey
        this.store = store
        this.appStore = appStore
        this.addMetrics()
        this.applyDefaults()
    }

    applyDefaults = () => {
        this.scope = this.scope ?? 'product'
        this.benchmarkType = this.benchmarkType ?? 'YOY'
    }

    addMetrics = () => {
        if (Object.keys(this.multiQuestion[0]).length) {
            this.metrics = this.multiQuestion.map((q, i) => new NewWidgetMetric({
                industry: q.industry || this.industry,
                respondentType: q.respondentType || this.respondentType,
                surveyType: q.surveyType || this.surveyType,
                productType: q.productType || this.productType,
                questionId: q.value || q.questionId,
            }, this.store, this.appStore, i))
            return
        }
        this.metrics = [
            new NewWidgetMetric({
                industry: this.industry,
                respondentType: this.respondentType,
                surveyType: this.surveyType,
                productType: this.productType,
                questionId: this.questionId,
            },
            this.store,
            this.appStore,
            0),
        ]
    }

    addMetric = (config) => {
        this.metrics.push(new NewWidgetMetric(
            config ?? {},
            this.store,
            this.appStore,
            this.metrics.length,
        ))
    }

    removeMetric = (index) => {
        this.metrics.splice(index, 1)
    }

    removeAllMetricsButFirst = () => {
        if (this.metrics.length < 2) return
        this.metrics = [this.metrics[0]]
    }

    @action
    changeNewWidgetBulk = (changes) => {
        changes.forEach((c) => { this[c.field] = c.value })
    }

    setIsNumeric = (question, oId) => {
        const numeric = this.getIsNumeric(question, oId)
        this.isNumeric = numeric
    }

    getIsNumeric = (question, oId) => {
        const { questionTypeCode } = question

        if (['I', 'NF', 'NF - row', 'FM', 'PC', 'OD', 'OD - row'].includes(questionTypeCode)) {
            return 'numeric'
        }

        if (['RD'].includes(questionTypeCode)) {
            const { renderNPS } = question.settings.frontend
            const kpiType = R.pathOr('', ['metadata', 'kpiType'], question)
            if (renderNPS || kpiType === 'NPS') {
                return 'nps'
            }
            const { hasNumericCodes } = question.optionSet
            return hasNumericCodes ? 'numeric' : 'nonNumeric'
        }

        if (['CH', 'SB', 'OD'].includes(questionTypeCode)) {
            const { hasNumericCodes } = question.optionSet
            return hasNumericCodes ? 'numeric' : 'nonNumeric'
        }

        if (['TB', 'TB - row'].includes(questionTypeCode)) {
            const { hasNumericCodes } = question.optionSetX
            return hasNumericCodes ? 'numeric' : 'nonNumeric'
        }

        if (['SG'].includes(questionTypeCode)) {
            return oId === 6 ? 'numericEvent' : 'numeric'
        }

        if (['TX', 'TA'].includes(questionTypeCode)) {
            return 'nonNumeric'
        }
        return ''
    }

    @action
    setChart = (chart, defaultSize) => {
        this.chartType = chart
        if (defaultSize) {
            this.defaultSize = defaultSize
        }
    }

    setChartSize = (size) => {
        this.defaultSize = size
    }

    get chartRefreshFields() {
        return {
            metrics: this.metrics.map((metric) => metric.toStandardised()),
            kpiType: this.kpiType,
            scope: this.scope,
            benchmarkType: this.benchmarkType,
            customTag: this.customTag,
            filter: this.filter,
            isQuadrantAnalysis: this.chartType === 'quadrantAnalysis',
            optionFilter: this.optionFilter,
            tagOptions: this.tagOptions,
        }
    }

    newWidgetToWidget = () => {
        const { langcode } = this.appStore
        const multiMetricWithSelectedData = this.metrics.filter((m) => m.question)
        if (this.metrics.length === 1 || multiMetricWithSelectedData.length === 1) {
            const metric = this.metrics[0]
            const isKpi = R.pathOr(false, ['question', 'type'], metric) === 'KPI'
            // const isMatrix = isKpi ? R.pathOr(false, ['question', 'isMatrixKpi'], metric) : false
            // const KpiTypeOverride = isMatrix ? 'LMAT' : R.pathOr(undefined, ['question', 'value'], metric)
            const metricFields = metric.toStandardised()
            return {
                kpiType: isKpi ? R.pathOr(undefined, ['question', 'value'], metric) : this.kpiType,
                chartType: this.chartType,
                chartSubType: this.chartSubType,
                title: this.title || R.pathOr('', ['question', 'content', 'shortTitle', langcode, 'text'], metric),
                width: this.width,
                benchmarkType: this.benchmarkType,
                scope: this.scope,
                filter: this.filter,
                widgetRenderSettings: this.widgetRenderSettings,
                key: uuidv4(),
                customTag: this.customTag,
                tagOptions: this.tagOptions,
                optionFilter: this.optionFilter,
                includeRegistrationData: this.includeRegistrationData,
                nonQuestionType: isKpi ? 'kpi' : undefined,
                ...metricFields,
                branchFilter: this.branchFilter,
            }
        }
        if (this.metrics.length > 1) {
            const firstMetric = this.metrics[0]
            const { name, ...firstMetricData } = firstMetric.toStandardised()
            const isQuadrantAnalysis = this.chartType === 'quadrantAnalysis' && [2, 3].includes(this.metrics.length)
            const title = isQuadrantAnalysis
                ? 'Bubble chart'
                : this.title || R.pathOr('', ['question', 'content', 'shortTitle', langcode, 'text'], firstMetric)

            // if (multimetric && YoY) we want year based series, else data-source based series
            const kpiTypeOverwrite = isQuadrantAnalysis || this.benchmarkType === 'YOY' ? this.kpiType : 'multiQuestion'

            return {
                kpiType: kpiTypeOverwrite,
                chartType: this.chartType,
                title,
                width: this.width,
                benchmarkType: this.benchmarkType,
                scope: this.scope,
                filter: this.filter,
                widgetRenderSettings: this.widgetRenderSettings,
                key: uuidv4(),
                customTag: this.customTag,
                tagOptions: this.tagOptions,
                optionFilter: this.optionFilter,
                includeRegistrationData: this.includeRegistrationData,
                nonQuestionType: isQuadrantAnalysis ? 'quadrantAnalysis' : 'multiQuestion',
                multiQuestion: this.metrics.map((metric) => (
                    metric.toStandardised(isQuadrantAnalysis)
                )),
                ...firstMetricData,
                name: this.chartType === 'quadrantAnalysis' ? 'Bubble chart' : name,
                branchFilter: this.branchFilter,
            }
        }
    }

    @action
    refreshWidgetKey = () => this.refreshKey = uuidv4()
}

export default NewWidget
